import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type ApiKey, type ViewerKey } from '../api';
import { CopyField, Icon, useNow, when } from '../ui';

export function Keys() {
  const qc = useQueryClient();
  const now = useNow(30_000);
  const q = useQuery({ queryKey: ['keys'], queryFn: () => api<{ data: ApiKey[] }>('/v1/api-keys') });
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<{ name: string; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const k = await api<ApiKey & { key: string }>('/v1/api-keys', { method: 'POST', json: { name: name.trim() } });
      setFresh({ name: k.name, key: k.key });
      setName('');
      await qc.invalidateQueries({ queryKey: ['keys'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the key.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (k: ApiKey) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
    try {
      await api(`/v1/api-keys/${k.id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['keys'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke the key.');
    }
  };

  return (
    <section className="page">
      <div>
        <h1>API keys</h1>
        <p className="sub">Your server uses these to create streams and mint playback links. Keep them out of browsers and apps.</p>
      </div>

      {fresh && (
        <section className="card secret-panel" aria-labelledby="h-fresh">
          <div className="card-head" style={{ alignItems: 'flex-start' }}>
            <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'nowrap' }}>
              <span style={{ color: 'var(--warn)', marginTop: 2 }}>
                <Icon name="lock" size={18} />
              </span>
              <div>
                <h2 id="h-fresh" style={{ fontSize: 16 }}>
                  Copy "{fresh.name}" now
                </h2>
                <p className="muted" style={{ margin: '4px 0 0' }}>It is shown only this once.</p>
              </div>
            </div>
            <button type="button" className="btn primary" onClick={() => setFresh(null)}>
              I've saved it
            </button>
          </div>
          <CopyField label="API key" value={fresh.key} oneLine secret />
        </section>
      )}

      <form className="card" onSubmit={create}>
        <h2>Create a key</h2>
        <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'nowrap' }}>
          <label className="field" style={{ flexGrow: 1 }}>
            Name
            <input className="input" required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="Production backend" />
          </label>
          <button type="submit" className="btn primary" disabled={busy} style={{ height: 40 }}>
            {busy ? 'Creating…' : 'Create key'}
          </button>
        </div>
        {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
      </form>

      {q.isPending ? (
        <p className="muted">Loading…</p>
      ) : q.error ? (
        <p className="error-text">{q.error.message}</p>
      ) : q.data.data.length === 0 ? (
        <div className="table">
          <div className="empty">No keys yet. Create one to use the API.</div>
        </div>
      ) : (
        <div className="table keys" role="table" aria-label="API keys">
          <div className="tr th" role="row">
            <span role="columnheader">Name</span>
            <span role="columnheader">Key</span>
            <span role="columnheader">Created</span>
            <span role="columnheader">Last used</span>
            <span role="columnheader" />
          </div>
          {q.data.data.map((k) => (
            <div key={k.id} className="tr" role="row">
              <span role="cell" style={{ fontWeight: 500 }}>{k.name}</span>
              <span role="cell" className="cell-mono">{k.redacted}</span>
              <span role="cell" className="muted">{when(k.created_at, now)}</span>
              <span role="cell" className="muted">{k.last_used_at ? when(k.last_used_at, now) : 'Never'}</span>
              <span role="cell" style={{ textAlign: 'right' }}>
                <button type="button" className="btn small" onClick={() => revoke(k)}>
                  Revoke
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <ViewerKeys />
    </section>
  );
}

/**
 * Viewer keys (docs: /viewer-tokens): your server signs a short token per
 * logged-in viewer with one, so chat knows who they are.
 */
function ViewerKeys() {
  const qc = useQueryClient();
  const now = useNow(30_000);
  const q = useQuery({ queryKey: ['viewer-keys'], queryFn: () => api<{ data: ViewerKey[] }>('/v1/viewer-keys') });
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<{ name: string; id: string; secret: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const k = await api<ViewerKey & { secret: string }>('/v1/viewer-keys', { method: 'POST', json: { name: name.trim() } });
      setFresh({ name: k.name, id: k.id, secret: k.secret });
      setName('');
      await qc.invalidateQueries({ queryKey: ['viewer-keys'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the key.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (k: ViewerKey) => {
    if (!window.confirm(`Delete "${k.name}"? Viewers signed in with it keep their chat session; new sign-ins with it are refused.`)) return;
    try {
      await api(`/v1/viewer-keys/${k.id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['viewer-keys'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the key.');
    }
  };

  return (
    <>
      <div style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: 20 }}>Viewer keys</h2>
        <p className="sub">
          If your viewers log in to your site, your server can tell us who they are: their real name in chat, bans that follow the person, and
          moderators from your own staff. It signs a short token for each viewer with one of these keys.{' '}
          <a href="https://docs.justonair.com/viewer-tokens" target="_blank" rel="noreferrer">
            How it works
          </a>
        </p>
      </div>

      {fresh && (
        <section className="card secret-panel" aria-labelledby="h-fresh-vk">
          <div className="card-head" style={{ alignItems: 'flex-start' }}>
            <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'nowrap' }}>
              <span style={{ color: 'var(--warn)', marginTop: 2 }}>
                <Icon name="lock" size={18} />
              </span>
              <div>
                <h2 id="h-fresh-vk" style={{ fontSize: 16 }}>
                  Copy "{fresh.name}" now
                </h2>
                <p className="muted" style={{ margin: '4px 0 0' }}>The secret is shown only this once. It goes on your server, never in a web page.</p>
              </div>
            </div>
            <button type="button" className="btn primary" onClick={() => setFresh(null)}>
              I've saved it
            </button>
          </div>
          <CopyField label="Key id (the token's kid)" value={fresh.id} oneLine />
          <CopyField label="Secret" value={fresh.secret} oneLine secret />
        </section>
      )}

      <form className="card" onSubmit={create}>
        <h2>Create a viewer key</h2>
        <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'nowrap' }}>
          <label className="field" style={{ flexGrow: 1 }}>
            Name
            <input className="input" required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="Academy site" />
          </label>
          <button type="submit" className="btn primary" disabled={busy} style={{ height: 40 }}>
            {busy ? 'Creating…' : 'Create key'}
          </button>
        </div>
        {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
      </form>

      {q.isPending ? (
        <p className="muted">Loading…</p>
      ) : q.error ? (
        <p className="error-text">{q.error.message}</p>
      ) : q.data.data.length === 0 ? (
        <div className="table">
          <div className="empty">No viewer keys. You only need one if your viewers log in to your site.</div>
        </div>
      ) : (
        <div className="table keys" role="table" aria-label="Viewer keys">
          <div className="tr th" role="row">
            <span role="columnheader">Name</span>
            <span role="columnheader">Key id</span>
            <span role="columnheader">Created</span>
            <span role="columnheader">Last used</span>
            <span role="columnheader" />
          </div>
          {q.data.data.map((k) => (
            <div key={k.id} className="tr" role="row">
              <span role="cell" style={{ fontWeight: 500 }}>{k.name}</span>
              <span role="cell" className="cell-mono">{k.id}</span>
              <span role="cell" className="muted">{when(k.created_at, now)}</span>
              <span role="cell" className="muted">{k.last_used_at ? when(k.last_used_at, now) : 'Never'}</span>
              <span role="cell" style={{ textAlign: 'right' }}>
                <button type="button" className="btn small" onClick={() => remove(k)}>
                  Delete
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
